import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { getPersonaById, type Persona } from "@/data/personas";
import type { EventCategory } from "@/types";

export interface PersonaContextValue {
  persona: Persona | null;
  personaId: string | null;
  setPersona: (personaId: string | null) => void;
  getTerminology: (key: keyof Persona["terminology"]) => string;
  getCategories: () => EventCategory[];
  getFeatures: () => Persona["features"];
  isLoaded: boolean;
}

const defaultContext: PersonaContextValue = {
  persona: null,
  personaId: null,
  setPersona: () => {},
  getTerminology: (key) => key,
  getCategories: () => [],
  getFeatures: () => ({
    smartLinks: true,
    analytics: true,
    teamView: true,
    nlpScheduling: true,
  }),
  isLoaded: false,
};

export const PersonaContext = createContext<PersonaContextValue>(defaultContext);

const PERSONA_STORAGE_KEY = "synqus_selected_persona";

export function PersonaProvider({ children }: { children: ReactNode }) {
  const [personaId, setPersonaId] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load persona from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(PERSONA_STORAGE_KEY);
    if (stored) {
      const persona = getPersonaById(stored);
      if (persona) {
        setPersonaId(stored);
      }
    }
    setIsLoaded(true);
  }, []);

  // Persist persona choice to localStorage
  const handleSetPersona = (id: string | null) => {
    setPersonaId(id);
    if (id) {
      localStorage.setItem(PERSONA_STORAGE_KEY, id);
    } else {
      localStorage.removeItem(PERSONA_STORAGE_KEY);
    }
  };

  const persona = personaId ? getPersonaById(personaId) ?? null : null;

  const getTerminology = (key: keyof Persona["terminology"]): string => {
    if (!persona) {
      // Default fallbacks when no persona selected
      switch (key) {
        case "attendees":
          return "attendees";
        case "meeting":
          return "meeting";
        case "booking":
          return "booking";
        case "client":
          return "participant";
      }
    }
    return persona.terminology[key];
  };

  const getCategories = (): EventCategory[] => {
    if (!persona) return [];
    return persona.categories;
  };

  const getFeatures = (): Persona["features"] => {
    if (!persona) {
      return {
        smartLinks: true,
        analytics: true,
        teamView: true,
        nlpScheduling: true,
      };
    }
    return persona.features;
  };

  return (
    <PersonaContext.Provider
      value={{
        persona,
        personaId,
        setPersona: handleSetPersona,
        getTerminology,
        getCategories,
        getFeatures,
        isLoaded,
      }}
    >
      {children}
    </PersonaContext.Provider>
  );
}

export function usePersona(): PersonaContextValue {
  const ctx = useContext(PersonaContext);
  if (!ctx) throw new Error("usePersona must be used within PersonaProvider");
  return ctx;
}
